/**
 * Constant-time operations for quantum cryptography
 * Prevents timing-based side-channel attacks
 */

/**
 * Constant-time equality comparison
 * Returns true if arrays are equal, false otherwise
 * Time taken is independent of array contents
 */
export function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) {
    return false
  }
  
  let result = 0
  for (let i = 0; i < a.length; i++) {
    result |= a[i] ^ b[i]
  }
  
  return result === 0
}

/**
 * Constant-time byte selection
 * Returns a if condition is 1, b if condition is 0
 * No branching based on condition value
 */
export function constantTimeSelect(
  condition: number,
  a: Uint8Array,
  b: Uint8Array
): Uint8Array {
  if (a.length !== b.length) {
    throw new Error('Arrays must have same length')
  }
  
  const mask = (-condition) & 0xff
  const result = new Uint8Array(a.length)
  
  for (let i = 0; i < a.length; i++) {
    result[i] = (a[i] & mask) | (b[i] & ~mask)
  }
  
  return result
}

/**
 * Constant-time comparison
 * Returns 1 if a > b, -1 if a < b, 0 if equal
 * Time independent of values
 */
export function constantTimeCompare(a: Uint8Array, b: Uint8Array): number {
  if (a.length !== b.length) {
    throw new Error('Arrays must have same length')
  }
  
  let gt = 0
  let eq = 1
  
  for (let i = a.length - 1; i >= 0; i--) {
    const diff = a[i] - b[i]
    const isGt = ((diff >>> 31) ^ 1) & (diff !== 0 ? 1 : 0)
    const isLt = (diff >>> 31) & 1
    
    gt = (gt & eq) | (isGt & eq)
    eq = eq & (diff === 0 ? 1 : 0)
  }
  
  const lt = (gt ^ 1) & (eq ^ 1)
  return gt - lt
}

/**
 * Constant-time modular reduction
 * Reduces value modulo prime without timing leaks
 */
export function constantTimeModulo(value: bigint, modulus: bigint): bigint {
  // Use blinding to prevent timing attacks
  const blindingFactor = generateBlindingFactor(modulus)
  const blinded = (value * blindingFactor) % modulus
  const blindingInverse = modInverse(blindingFactor, modulus)
  return (blinded * blindingInverse) % modulus
}

/**
 * Generate cryptographically secure blinding factor
 */
function generateBlindingFactor(modulus: bigint): bigint {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  
  let factor = 0n
  for (const byte of bytes) {
    factor = (factor << 8n) | BigInt(byte)
  }
  
  // Ensure factor is coprime with modulus
  factor = factor % modulus
  if (factor === 0n) factor = 1n
  
  return factor
}

/**
 * Modular multiplicative inverse using extended Euclidean algorithm
 */
function modInverse(a: bigint, m: bigint): bigint {
  let [old_r, r] = [a, m]
  let [old_s, s] = [1n, 0n]
  
  while (r !== 0n) {
    const quotient = old_r / r
    ;[old_r, r] = [r, old_r - quotient * r]
    ;[old_s, s] = [s, old_s - quotient * s]
  }
  
  return old_s < 0n ? old_s + m : old_s
}

/**
 * Dilithium-specific constant-time operations
 */
export class DilithiumConstantTime {
  /**
   * Constant-time polynomial coefficient comparison
   */
  static compareCoefficients(a: Int32Array, b: Int32Array): boolean {
    if (a.length !== b.length) return false
    
    let diff = 0
    for (let i = 0; i < a.length; i++) {
      diff |= a[i] ^ b[i]
    }
    
    return diff === 0
  }
  
  /**
   * Constant-time rejection sampling
   * Ensures timing doesn't leak information about rejected values
   */
  static rejectSample(value: Uint8Array, threshold: number): boolean {
    // Convert to number without branching
    let val = 0
    for (let i = 0; i < Math.min(4, value.length); i++) {
      val |= value[i] << (i * 8)
    }
    
    // Constant-time threshold check
    const diff = threshold - val
    const sign = diff >> 31
    return (sign & 1) === 0
  }
  
  /**
   * Mask polynomial coefficients in constant time
   */
  static maskPolynomial(
    poly: Int32Array,
    mask: Uint8Array
  ): Int32Array {
    const result = new Int32Array(poly.length)
    const maskBits = expandMask(mask, poly.length)
    
    for (let i = 0; i < poly.length; i++) {
      result[i] = poly[i] & maskBits[i]
    }
    
    return result
  }
}

/**
 * Expand mask to required length
 */
function expandMask(mask: Uint8Array, length: number): Int32Array {
  const result = new Int32Array(length)
  const maskLength = mask.length
  
  for (let i = 0; i < length; i++) {
    const maskIndex = i % maskLength
    result[i] = mask[maskIndex] ? -1 : 0
  }
  
  return result
}

/**
 * Timing-safe memory comparison
 * Uses crypto.timingSafeEqual when available
 */
export function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) {
    return false
  }
  
  // Use native timing-safe comparison if available
  if (typeof crypto !== 'undefined' && 'timingSafeEqual' in crypto) {
    return (crypto as any).timingSafeEqual(a, b)
  }
  
  // Fallback to our implementation
  return constantTimeEqual(a, b)
}

/**
 * Clear sensitive data from memory
 * Overwrites with random data to prevent recovery
 */
export function secureClear(data: Uint8Array | Int32Array): void {
  // First pass: overwrite with zeros
  for (let i = 0; i < data.length; i++) {
    data[i] = 0
  }
  
  // Second pass: overwrite with random values
  const random = new Uint8Array(data.length * (data instanceof Int32Array ? 4 : 1))
  crypto.getRandomValues(random)
  
  if (data instanceof Int32Array) {
    for (let i = 0; i < data.length; i++) {
      data[i] = (random[i * 4] << 24) | 
                (random[i * 4 + 1] << 16) |
                (random[i * 4 + 2] << 8) |
                random[i * 4 + 3]
    }
  } else {
    for (let i = 0; i < data.length; i++) {
      data[i] = random[i]
    }
  }
  
  // Third pass: overwrite with ones
  for (let i = 0; i < data.length; i++) {
    data[i] = data instanceof Int32Array ? -1 : 0xff
  }
  
  // Final pass: zeros again
  for (let i = 0; i < data.length; i++) {
    data[i] = 0
  }
}

export default {
  constantTimeEqual,
  constantTimeSelect,
  constantTimeCompare,
  constantTimeModulo,
  DilithiumConstantTime,
  timingSafeEqual,
  secureClear
}